import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { CesiumContextProvider, useCesium } from '../context/CesiumContext';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';
// Module-level translate: used inside the one-shot `activeAction` effect below so its
// toasts pick up the current language without making `t` a dependency — `activeAction`
// is never reset after being handled, so re-running the effect on a language switch
// would re-fire its side effects (duplicate toast, re-triggered zoom/unlink).
import { t as translate } from '@/i18n';

// Components
import CesiumInitializer from './3dViewer/CesiumInitializer';
import TilesLoader from './loader/TilesLoader';
import DamagesLoader from './drawingTools/DamagesLoader';
import PanoramaLoader from './loader/PanoramaLoader';
import MapToolbar from './drawingTools/MapToolbar';
import { DamagePopup } from './drawingTools/DamagePopup';
import MapLoader from './loader/MapLoader';
import KrpanoViewer from './panaroma/KrpanoViewer';
import LayerControl from './drawingTools/LayerControl';
import SensorLoader from './loader/SensorLoader';
import { SensorChartOverlay } from './loader/SensorChartOverlay';
import UndergroundControls from './loader/UndergroundControls';

// Helpers & Hooks
import {
	findDamagePolygon,
	highlightDamageOnMap,
	getDamagePolygonArea,
	getDamagePolygonPerimeter,
} from '@/helper/cesiumHelper';
import { usePolygonMutations } from '@/hooks/use-polygon-mutations';
import type { MappedAssetSensor } from '@/common/asset-sensor';
import { MapNavigationControls } from './3dViewer/MapNavigationControls';
import { PerformanceMonitor } from './3dViewer/PerformanceMonitor';
import { InitialZoomController } from './loader/InitialZoomController';
import { AssetPositionZoom } from './loader/AssetPositionZoom';
import { useDeletedPreview } from '@/hooks/use-deleted-preview';
import { useAssetSettings } from '@/hooks/use-asset-settings';
import { useLimitBoxAvailable } from '@/hooks/use-limit-box-available';
import { UploadDamagePopup } from './drawingTools/UploadDamagePopup';
import { useBulkDamageUpload } from '@/hooks/use-bulk-damage-upload';
import ClippingBoxManager from './drawingTools/ClippingBoxManager';
import { TilesetLocationEditor } from './data-upload/TilesetLocationEditor';
import { IfcFeatureProvider, useIfcFeatures } from '../context/IfcFeatureContext';
import IfcPickHandler from './ifc/IfcPickHandler';
import IfcPanel from './ifc/IfcPanel';
type MapAction = {
	type: 'unlink' | 'zoom' | 'refresh' | 'cancel' | 'delete' | 'save';
	payload?: any;
};

type CesiumViewerProps = {
	onPolygonCreated?: (polygons: any[]) => void;
	activeAction?: MapAction | null;
	activeDamage?: any;
	registerSizeCalculator?: (fn: (damageId: string) => any) => void;
	selectedSensor?: MappedAssetSensor | null;
	onSelectSensor?: (sensor: MappedAssetSensor | null) => void;
};

type UploadTarget = { damageId: string; inspectionId?: string };

const CesiumViewerContent: React.FC<CesiumViewerProps> = ({
	onPolygonCreated,
	activeAction,
	activeDamage,
	registerSizeCalculator,
	selectedSensor,
	onSelectSensor,
}) => {
	const { t } = useTranslation();
	const { assetId, inspectionId, damageId } = useParams();
	// A deleted asset previewed read-only (GH #331): the base map at its position, none of its data
	const isDeletedAsset = useDeletedPreview('asset');
	const { getSettings } = useAssetSettings(assetId);
	const savedSettings = getSettings();

	const {
		selectedDamage,
		setSelectedDamage,
		selectedDamageEntity,
		setSelectedDamageEntity,
		drawMode,
		setDrawMode,
		setActiveDamageContext,
		subSurfaceLayers,
		setUndergroundEnabled,
		viewer,
	} = useCesium();
	const { closePanel: closeIfcPanel } = useIfcFeatures();

	const [selectedScene, setSelectedScene] = useState<string | null>(null);
	const [currentBaseMapId, setCurrentBaseMapId] = useState<string>(savedSettings.baseMapId);
	const [internalSelectedSensor, setInternalSelectedSensor] = useState<MappedAssetSensor | null>(
		null
	);

	const [isUploadMode, setIsUploadMode] = useState(false);
	const [uploadSelectedItems, setUploadSelectedItems] = useState<UploadTarget[]>([]);

	const [isClippingMode, setIsClippingMode] = useState(false);
	const [loadedTilesets, setLoadedTilesets] = useState<any[]>([]);
	const canUseLimitBox = useLimitBoxAvailable(viewer, loadedTilesets);

	const { upload: uploadBulk, isLoading: isBulkUploading } = useBulkDamageUpload();

	const navigate = useNavigate();
	const activeSensor = selectedSensor !== undefined ? selectedSensor : internalSelectedSensor;

	const { unlinkPolygon } = usePolygonMutations({
		assetId: assetId || '',
		inspectionId: inspectionId || '',
		damageId: damageId || '',
		onUnlinkSuccess: () => {
			// Body, not an expression: an arrow that returns the call keeps its value
			// in use, so the production build cannot drop it.
			console.log('Polygon un-link success');
		},
	});

	const handleToggleUploadMode = () => {
		if (isUploadMode) {
			setIsUploadMode(false);
			setUploadSelectedItems([]);
		} else {
			setSelectedDamage(null);
			setSelectedDamageEntity(null);
			setDrawMode('NONE');
			setIsClippingMode(false); // Turn off clipping if uploading
			setIsUploadMode(true);
		}
	};

	const handleToggleClippingMode = () => {
		if (loadedTilesets.length === 0) {
			toast.error(t('viewer.toast.waitForModels'));
			return;
		}
		setIsClippingMode(!isClippingMode);
		// Turn off other modes
		if (!isClippingMode) {
			setIsUploadMode(false);
			setDrawMode('NONE');
		}
	};

	const handleSaveUpload = async (files: File[]) => {
		if (!assetId) return;
		try {
			await Promise.all(
				files.map((file) =>
					uploadBulk({
						file,
						targets: uploadSelectedItems,
						assetId: assetId,
					})
				)
			);
			setIsUploadMode(false);
			setUploadSelectedItems([]);
		} catch (error) {
			console.error('Batch upload failed', error);
			toast.error(t('viewer.toast.uploadFailed'));
		}
	};

	const calculateDamageSize = useCallback(
		(targetDamageId: string, measurementType: 'area' | 'perimeter') => {
			const viewer = (window as any).viewer;
			if (!viewer) return null;
			if (measurementType === 'area') {
				return getDamagePolygonArea(viewer, targetDamageId);
			} else {
				return getDamagePolygonPerimeter(viewer, targetDamageId);
			}
		},
		[]
	);

	useEffect(() => {
		if (savedSettings.undergroundEnabled) {
			setUndergroundEnabled(true);
		}
	}, []);

	useEffect(() => {
		if (registerSizeCalculator) {
			registerSizeCalculator(() => calculateDamageSize);
		}
	}, [registerSizeCalculator, calculateDamageSize]);

	useEffect(() => {
		if (drawMode === 'CANCEL') {
			setSelectedDamage(null);
			setSelectedDamageEntity(null);
			if (onPolygonCreated) onPolygonCreated([]);
		}
	}, [drawMode]);

	// The top-right slot holds one inspector. A damage popup — being drawn, or selected —
	// takes it from the IFC panel; the other direction is handled where the panel is opened.
	useEffect(() => {
		const damageOwnsSlot =
			!!selectedDamageEntity || !!selectedDamage || drawMode === 'INSERT' || drawMode === 'EDIT';
		if (damageOwnsSlot) closeIfcPanel();
	}, [selectedDamageEntity, selectedDamage, drawMode, closeIfcPanel]);

	useEffect(() => {
		setActiveDamageContext(activeDamage);
	}, [activeDamage]);

	useEffect(() => {
		if (!activeAction) return;
		const viewer = (window as any).viewer;
		if (!viewer) return;

		if (activeAction.type === 'unlink') {
			const damageId = activeAction.payload?.damageId;
			if (damageId) {
				const entity = findDamagePolygon(viewer, damageId);
				if (entity) {
					unlinkPolygon(entity.id);
					setDrawMode('CANCEL');
				} else {
					toast.error(translate('viewer.toast.polygonNotFound'));
				}
			}
		} else if (activeAction.type === 'zoom') {
			const damageId = activeAction.payload?.damageId;
			if (damageId) {
				const entity = findDamagePolygon(viewer, damageId);
				if (entity) {
					toast.success(translate('viewer.toast.zoomingToDamage'));
					highlightDamageOnMap(viewer, damageId);
				} else {
					toast.error(translate('viewer.toast.damageLocationNotFound'));
				}
			}
		} else if (['cancel', 'delete', 'save'].includes(activeAction.type)) {
			setDrawMode('CANCEL');
		}
	}, [activeAction]);

	const handleDrawComplete = (coords: any[], type: 'damage' | 'inspection') => {
		setSelectedDamage(coords);
		setSelectedDamageEntity(null);
		if (onPolygonCreated) onPolygonCreated(coords);
		if (type === 'inspection' && inspectionId) {
			navigate(`/assets/${assetId}/inspections/${inspectionId}/damages/new`);
		}
	};

	const handleSensorChange = (sensor: MappedAssetSensor | null) => {
		setInternalSelectedSensor(sensor);
		if (onSelectSensor) onSelectSensor(sensor);
	};

	if (isDeletedAsset) {
		return (
			<div className="relative w-full h-[calc(100vh-64px)] overflow-hidden bg-black">
				<div className="w-full h-full relative">
					<CesiumInitializer>
						{assetId && <AssetPositionZoom assetId={assetId} />}
						<MapLoader status={false} />
						<LayerControl selectedBaseMapId={currentBaseMapId} onBaseMapChange={setCurrentBaseMapId} />
						<MapNavigationControls />
					</CesiumInitializer>
				</div>
			</div>
		);
	}

	return (
		<div className="relative w-full h-[calc(100vh-64px)] overflow-hidden bg-black">
			<div
				className={`w-full h-full transition-opacity duration-300 ${selectedScene ? 'opacity-0 pointer-events-none absolute inset-0' : 'opacity-100 relative'}`}
			>
				<CesiumInitializer>
					{/* Pass setter to TilesLoader to grab the model reference */}
					<TilesLoader onTilesetsLoaded={setLoadedTilesets} />

					{assetId && (
						<DamagesLoader
							assetId={assetId}
							inspectionId={inspectionId}
							isUploadMode={isUploadMode}
							selectedUploadItems={uploadSelectedItems}
							onUploadSelectionChange={setUploadSelectedItems}
						/>
					)}

					{assetId && (
						<SensorLoader
							assetId={assetId}
							selectedSensor={activeSensor}
							onSelectSensor={handleSensorChange}
						/>
					)}
					{assetId && activeSensor && (
						<SensorChartOverlay
							assetId={assetId}
							selectedSensor={activeSensor}
							onClose={() => handleSensorChange(null)}
						/>
					)}
					{assetId && <InitialZoomController assetId={assetId} inspectionId={inspectionId} />}
					<MapLoader status={false} />

					{/* Updated Toolbar */}
					<MapToolbar
						onDamageDrawComplete={handleDrawComplete}
						isUploadMode={isUploadMode}
						onToggleUploadMode={handleToggleUploadMode}
						isClippingMode={isClippingMode}
						onToggleClippingMode={handleToggleClippingMode}
						canUseLimitBox={canUseLimitBox}
					/>

					{/* Clipping Manager Component */}
					<ClippingBoxManager
						tilesets={loadedTilesets}
						enabled={isClippingMode}
						onClose={() => setIsClippingMode(false)}
					/>

					{!isUploadMode && (selectedDamageEntity || selectedDamage) && (
						<DamagePopup onClose={() => setDrawMode('CANCEL')} />
					)}
					<IfcPanel />
					<UploadDamagePopup
						isOpen={isUploadMode}
						onClose={() => handleToggleUploadMode()}
						selectionCount={uploadSelectedItems.length}
						onSave={handleSaveUpload}
						isSaving={isBulkUploading}
					/>
					<PanoramaLoader onOpenPanorama={(sceneName) => setSelectedScene(sceneName)} />
					<IfcPickHandler />
					<LayerControl
						selectedBaseMapId={currentBaseMapId}
						onBaseMapChange={setCurrentBaseMapId}
					/>
					{subSurfaceLayers.length > 0 && (
						<UndergroundControls currentBaseMapId={currentBaseMapId} />
					)}
					<MapNavigationControls />
					{/* Frame timings and memory are a developer's tool. Same switch as the
					    Sentry debug route: on everywhere the app is being worked on, off
					    in production. */}
					{import.meta.env.VITE_SENTRY_ENVIRONMENT !== 'production' && <PerformanceMonitor />}
					<TilesetLocationEditor />
				</CesiumInitializer>
			</div>
			{selectedScene && (
				<div className="absolute inset-0 z-50 w-full h-full bg-black">
					<KrpanoViewer
						startScene={selectedScene}
						onClose={() => {
							setSelectedScene(null);
							const v = (window as any).viewer;
							if (v) v.forceResize();
						}}
					/>
				</div>
			)}
		</div>
	);
};

export default function CesiumViewer(props: CesiumViewerProps) {
	return (
		<CesiumContextProvider>
			<IfcFeatureProvider>
				<CesiumViewerContent {...props} />
			</IfcFeatureProvider>
		</CesiumContextProvider>
	);
}
